// @flow
import React from 'react';
import styled from 'styled-components';
import Avatar from '@material-ui/core/Avatar';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import { withTheme } from '@material-ui/core/styles';

import Typography from '@material-ui/core/Typography';
import Toggle from 'react-toggled';

import { withTranslation } from 'react-i18next';
import ScribeTranscriptWordContainer from './ScribeTranscriptWord';
import ScribeWordInputContainer from './ScribeWordInputContainer';
import { getText } from '../../utils/scribeUtils';
import AsrQueueingMessageLoader from '../../components/AsrQueueingMessageLoader';
import EditProfileModal from './EditProfileModal';

const isTextEqualExceptSelectedWord = (props1, props2, selectedWordIndex) => {
  const t1 = getText(
    // $FlowFixMe
    { texts: props1.texts, speechLang: props1.speechLang },
    props1.lang
  );
  const t2 = getText(
    // $FlowFixMe
    { texts: props2.texts, speechLang: props2.speechLang },
    props2.lang
  );

  const t1Words = t1.split(' ');
  const t2Words = t2.split(' ');
  if (t1Words.length !== t2Words.length) {
    return false;
  }
  if (
    t1Words.slice(0, selectedWordIndex).join(' ') !==
      t2Words.slice(0, selectedWordIndex).join(' ') ||
    t1Words.slice(selectedWordIndex + 1, t2Words.length).join(' ') !==
      t2Words.slice(selectedWordIndex + 1, t2Words.length).join(' ')
  ) {
    return false;
  }
  return true;
};

type Props = {
  activeFeatures: Array<string>,
  author: string,
  authorShow: boolean,
  currentPlatform: string,
  cursors: Array<Cursor>,
  fontSize: number,
  highlighted: boolean,
  id: string,
  indexes: Object,
  isSelected: boolean,
  lang: string,
  lightView: boolean,
  listened: boolean,
  muted: boolean,
  participantsHash: Array<Object>,
  scribe: boolean,
  scribeSelectTranscriptWord: Function,
  selectedWordIndex?: number,
  socketConnected: string,
  speakers: Object,
  speechLang: string,
  t: Function,
  texts: string,
  theme: Object,
  user: Object,
  wordBeingEdited: boolean,
};

const Transcript = styled.div`
  color: ${props => props.background};
  margin-bottom: 5px;
  line-height: ${props => props.lineHeight}px;
  font-size: ${props => props.fontSize}px;
`;

class ScribeTranscript extends React.Component<Props> {
  shouldComponentUpdate(nextProps) {
    const { selectedWordIndex } = this.props;
    const updatedKeys = [];
    Array.from(Object.keys(nextProps)).forEach(k => {
      const { [k]: value } = this.props;
      if (nextProps[k] !== value) {
        updatedKeys.push(k);
      }
    });

    if (
      updatedKeys.includes('texts') &&
      isTextEqualExceptSelectedWord(nextProps, this.props, selectedWordIndex) &&
      nextProps.wordBeingEdited
    ) {
      // eslint-disable-next-line
      for (let k of [
        'wordBeingEdited',
        'mutationsQueuedByHumanMutation',
        'cursors',
      ]) {
        if (updatedKeys.includes(k) && updatedKeys.length === 2) {
          return false;
        }
      }
      if (updatedKeys.length === 1) {
        return false;
      }
    }
    if (
      updatedKeys.length === 1 &&
      (updatedKeys.includes('wordBeingEdited') ||
        updatedKeys.includes('mutationsQueuedByHumanMutation'))
    ) {
      return false;
    }
    return true;
  }

  getBackground() {
    const { listened, theme } = this.props;
    if (listened) {
      if (theme.palette.type === 'light') {
        return '#952926';
      }
      return 'rgb(250, 200, 250)';
    }
    return theme.palette.text.primary;
  }

  render() {
    const {
      activeFeatures,
      author,
      authorShow,
      currentPlatform,
      cursors,
      fontSize,
      highlighted,
      id,
      isSelected,
      lang,
      lightView,
      muted,
      participantsHash: participants,
      scribe,
      scribeSelectTranscriptWord,
      selectedWordIndex,
      socketConnected,
      speakers,
      speechLang,
      t,
      texts,
      theme,
      user,
    } = this.props;

    let { indexes: human } = this.props;
    human = human || {};
    const participantsHash = participants
      ? participants.map(p => p.hash)
      : undefined;
    let charIndex = 0;
    // $FlowFixMe
    const text = getText({ texts, speechLang }, lang);
    const transcriptWords = text.split(' ');
    if ((muted || lightView || !isSelected) && !text) {
      return null;
    }

    const speaker: ?Speaker = speakers[author];
    const indexes = {};
    if (cursors && participantsHash) {
      cursors.forEach(cursor => {
        if (participantsHash.includes(cursor.mutatorHash)) {
          if (!indexes[cursor.index]) {
            indexes[cursor.index] = [];
          }
          indexes[cursor.index].push(cursor.mutatorHash);
        }
      });
    }
    const allowEdit =
      currentPlatform === 'scribe' ||
      (socketConnected === 'CONNECTED' &&
        !muted &&
        !lightView &&
        activeFeatures.includes('multi_edit_web'));
    if (!allowEdit) {
      // eslint-disable-next-line
      console.log({ socketConnected, muted, lightView, activeFeatures });
    }
    const lastHuman = human[(speechLang || lang).split('-')[0]];

    let color = theme.palette.error.light;
    if (theme.palette.type === 'light' && speaker && speaker.color) {
      ({ color } = speaker);
    }
    // $FlowFixMe
    if (theme.palette.type === 'dark' && speaker && speaker.colorBackground) {
      color = speaker.colorBackground;
    }
    if (speaker && speaker.id === user.avaId) {
      color =
        theme.palette.type === 'dark'
          ? theme.palette.ava.selfColorBlue
          : theme.palette.ava.selfColorBlueDark;
    }
    let authorName = author;
    if (speaker && speaker.userName) {
      authorName = speaker.userName;
    }
    if (speaker && speaker.id === user.avaId) {
      authorName = t('transcripts.you');
      if (user.userName === 'web guest') {
        authorName += t('transcripts.tapToSetYourName');
      }
    }

    return (
      <Transcript
        fontSize={fontSize}
        lineHeight={currentPlatform === 'scribe' ? fontSize * 1.5 : fontSize}
        background={this.getBackground()}
      >
        <Grid container wrap="nowrap" spacing={1}>
          <Hidden xsDown>
            <Grid item>
              {authorShow && speaker && speaker.userAvatar !== '' && (
                <Avatar
                  key="avatar"
                  src={speaker.userAvatar}
                  alt="speaker_photo"
                  style={{
                    width: fontSize,
                    height: fontSize,
                    marginTop: fontSize / 5,
                  }}
                />
              )}
              {currentPlatform === 'web' && (
                <Grid
                  style={{
                    width: fontSize,
                    height: fontSize,
                    marginTop: fontSize / 5,
                  }}
                />
              )}
            </Grid>
          </Hidden>
          <Grid item xs css={{ lineHeight: 1 }}>
            {authorShow && (
              <Toggle>
                {({ on, getTogglerProps, setOff }) => (
                  <>
                    <Typography
                      variant="h5"
                      style={{
                        color,
                        fontWeight: 'bold',
                        fontSize,
                        outline: 'none',
                        cursor:
                          speaker && speaker.id === user.avaId && 'pointer',
                      }}
                      {...getTogglerProps()}
                    >
                      <strong key="name">{authorName}</strong>
                    </Typography>
                    {speaker && speaker.id && speaker.id === user.avaId && (
                      <EditProfileModal open={on} close={setOff} />
                    )}
                  </>
                )}
              </Toggle>
            )}
            {((!muted && !lightView) || text) &&
              transcriptWords.map((wordText, i) => {
                const wordComp =
                  i === selectedWordIndex && allowEdit && isSelected ? (
                    // eslint-disable-next-line
                    <ScribeWordInputContainer key={`${i}-${wordText}`} />
                  ) : (
                    <ScribeTranscriptWordContainer
                      word={wordText}
                      cursor={user && user.features && user.features.scribe && indexes[i]}
                      participants={participants}
                      transcriptId={id}
                      wordIndex={i}
                      highlighted={highlighted}
                      allowEdit={allowEdit}
                      scribe={scribe}
                      unlocked={charIndex + wordText.length > lastHuman}
                      scribeSelectTranscriptWord={scribeSelectTranscriptWord}
                      // eslint-disable-next-line
                      key={`${i}-${wordText}`}
                    />
                  );
                charIndex += wordText.length + 1;
                return wordComp;
              })}
            <AsrQueueingMessageLoader transcriptId={id} />
          </Grid>
        </Grid>
      </Transcript>
    );
  }
}
export default withTranslation()(withTheme(ScribeTranscript));
